// ============================================
// JOBS LIST PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Search, Ship, MapPin, Calendar, DollarSign, Heart, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useJobs, useDebounce, useFavoriteJobs, useAddFavoriteJob, useRemoveFavoriteJob, useMyProfile, useMatchingJobs } from '@/hooks';
import { formatCurrency, formatRelativeTime, cn } from '@/utils';
import { MatchScoreBadge } from '@/components';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/jobs/')({
  component: JobsPage,
});

function JobsPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;

  const debouncedSearch = useDebounce(search, 300);

  const { data: jobsResponse, isLoading } = useJobs({
    page, limit,
    search: debouncedSearch || undefined,
    department: department || undefined,
    vesselType: vesselType || undefined,
  });

  const { data: profileResponse } = useMyProfile();
  const profile: any = (profileResponse as any)?.data || profileResponse;
  const profileCompleteness = profile?.profileCompleteness || 0;

  const { data: matchingResponse } = useMatchingJobs();
  
  const matchScoreMap = useMemo(() => {
    const map = new Map<string, number>();
    const matchingData: any = (matchingResponse as any)?.data || matchingResponse || [];
    const matchingJobs = Array.isArray(matchingData) ? matchingData : [];
    matchingJobs.forEach((item: any) => {
      const jobId = item.job?.id;
      const score = item.matchScore?.overallScore || item.matchScore || 0;
      if (jobId) map.set(jobId, score);
    });
    return map;
  }, [matchingResponse]);

  const { data: favoritesResponse } = useFavoriteJobs();
  const addFavorite = useAddFavoriteJob();
  const removeFavorite = useRemoveFavoriteJob();

  const jobsData: any = (jobsResponse as any)?.data || jobsResponse || {};
  const jobs: any[] = jobsData?.data || jobsData || [];
  const total = jobsData?.meta?.total || jobsData?.total || 0;
  const totalPages = jobsData?.meta?.totalPages || Math.ceil(total / limit);

  const favorites: any[] = (favoritesResponse as any)?.data || favoritesResponse || [];
  const favoriteJobIds = favorites.map((f: any) => f.job?.id || f.jobId);

  const toggleFavorite = (jobId: string) => {
    if (favoriteJobIds.includes(jobId)) {
      removeFavorite.mutate(jobId);
    } else {
      addFavorite.mutate(jobId);
    }
  };

  const departments = ['DECK', 'ENGINE', 'ELECTRICAL', 'CATERING'];
  const vesselTypes = [
    'BULK_CARRIER', 'TANKER', 'CONTAINER', 'GENERAL_CARGO', 'RO_RO',
    'LNG', 'LPG', 'CHEMICAL_TANKER', 'OFFSHORE', 'PASSENGER',
    'FERRY', 'TUG', 'YACHT', 'FISHING', 'RESEARCH', 'NAVY', 'OTHER'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('panel:jobs.title')}</h1>
          <p className="text-gray-500 mt-1">{t('panel:jobs.activeCount', { count: total })}</p>
        </div>
        {profileCompleteness < 100 && (
          <Link to="/profile" className="text-sm text-maritime-600 hover:text-maritime-700 flex items-center gap-1">
            {t('panel:jobs.profileIncomplete', { percent: profileCompleteness })}
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('panel:jobs.searchPlaceholder')}
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-full md:w-48"
            value={department}
            onChange={(e) => { setDepartment(e.target.value); setPage(1); }}
          >
            <option value="">{t('panel:jobs.allDepartments')}</option>
            {departments.map((key) => (
              <option key={key} value={key}>{t(`enums:department.${key}`)}</option>
            ))}
          </select>
          <select
            className="input w-full md:w-48"
            value={vesselType}
            onChange={(e) => { setVesselType(e.target.value); setPage(1); }}
          >
            <option value="">{t('panel:jobs.allVesselTypes')}</option>
            {vesselTypes.map((key) => (
              <option key={key} value={key}>{t(`enums:vesselType.${key}`, { defaultValue: key })}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Jobs List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="card p-12 text-center">
          <Ship className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('panel:jobs.noJobs')}</h3>
          <p className="text-gray-500">{t('panel:jobs.tryDifferentFilters')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job: any) => (
            <JobCard
              key={job.id}
              job={job}
              isFavorite={favoriteJobIds.includes(job.id)}
              onToggleFavorite={() => toggleFavorite(job.id)}
              matchScore={matchScoreMap.get(job.id) || 0}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary btn-sm">
            <ChevronLeft className="w-4 h-4" />{t('common:previous')}
          </button>
          <span className="text-sm text-gray-600">
            {t('common:pagination', { page, totalPages })}
          </span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-secondary btn-sm">
            {t('common:next')}<ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function JobCard({ job, isFavorite, onToggleFavorite, matchScore, t }: { 
  job: any; isFavorite: boolean; onToggleFavorite: () => void; matchScore: number; t: any;
}) {
  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-maritime flex items-center justify-center flex-shrink-0 bg-maritime-100">
          {job.company?.logoUrl ? (
            <img src={job.company.logoUrl} alt={job.company.name} className="w-10 h-10 object-contain" />
          ) : (
            <Ship className="w-7 h-7 text-maritime-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Link to={`/jobs/${job.slug}`} className="text-lg font-semibold text-gray-900 hover:text-maritime-600 line-clamp-1">
                {job.title}
              </Link>
              <p className="text-sm text-gray-600">{job.company?.name}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {matchScore > 0 && <MatchScoreBadge score={matchScore} size="sm" showLabel={false} />}
              <button
                onClick={(e) => { e.preventDefault(); onToggleFavorite(); }}
                className={cn(
                  'p-2 rounded-full transition-colors',
                  isFavorite ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'
                )}
              >
                <Heart className={cn('w-5 h-5', isFavorite && 'fill-current')} />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
            {job.vesselType && (
              <span className="flex items-center gap-1">
                <Ship className="w-4 h-4" />
                {t(`enums:vesselType.${job.vesselType}`, { defaultValue: job.vesselType })}
              </span>
            )}
            {(job.departurePort || job.company?.country) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {job.departurePort || job.company?.country}
              </span>
            )}
            {job.contractDuration && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {t('panel:jobs.monthsContract', { count: job.contractDuration })}
              </span>
            )}
            {(job.salaryMin || job.salaryMax) && (
              <span className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                {job.salaryMin && job.salaryMax
                  ? `${formatCurrency(job.salaryMin)} - ${formatCurrency(job.salaryMax)}`
                  : job.salaryMin
                    ? `${formatCurrency(job.salaryMin)}+`
                    : `${formatCurrency(job.salaryMax)} max`}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {job.createdAt ? formatRelativeTime(job.createdAt) : t('panel:jobs.new')}
            </span>
            <Link to={`/jobs/${job.slug}`} className="text-sm font-medium text-maritime-600 hover:text-maritime-700">
              {t('panel:jobs.viewDetails')} →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

